/**
 * EFIN-04: Event Finance Master Data Administration
 *
 * MDM-STD-01 compliant admin panel for the six event finance config types:
 *   EVENT_FINANCE_CATEGORY | COST_ALLOCATION_RULE | REVENUE_CLASSIFICATION
 *   MARGIN_THRESHOLD       | FINANCIAL_EXCEPTION_REASON | REPORTING_DIMENSION
 *
 * Capabilities:
 *   event.finance.manage  — create / edit / activate / deactivate
 *   event.finance.view    — list / detail / history (read-only)
 *
 * Restrictions:
 *   - event.finance.manage does NOT grant shared-cost approval or reversal.
 *   - Platform-owned items (tenant_id === "PLATFORM") cannot be deactivated by tenants.
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Search, Plus, RefreshCw, ChevronRight, X, Edit3,
  History, ToggleLeft, ToggleRight, Download, AlertTriangle,
  CheckCircle2, Info, Shield,
} from "lucide-react";
import { eventFinanceApi, formatError } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";

// ── Constants ─────────────────────────────────────────────────────────────────

const CONFIG_TYPES = [
  { id: "EVENT_FINANCE_CATEGORY",      label: "Finance Category",       description: "Classification of event-level revenue and cost line items" },
  { id: "COST_ALLOCATION_RULE",        label: "Cost Allocation Rule",   description: "Rules governing how shared costs are split across events" },
  { id: "REVENUE_CLASSIFICATION",      label: "Revenue Classification", description: "Customer invoice revenue type taxonomy" },
  { id: "MARGIN_THRESHOLD",            label: "Margin Threshold",       description: "Warning and alert thresholds for billed/forecast margins" },
  { id: "FINANCIAL_EXCEPTION_REASON",  label: "Exception Reason",       description: "Approved reason codes for financial exceptions and overrides" },
  { id: "REPORTING_DIMENSION",         label: "Reporting Dimension",    description: "Supplemental analytics dimensions for event finance reporting" },
];

const TYPE_BADGE_COLOR = {
  EVENT_FINANCE_CATEGORY:     { bg: "#EEF4FF", text: "#0176D3" },
  COST_ALLOCATION_RULE:       { bg: "#FFF7F0", text: "#EA580C" },
  REVENUE_CLASSIFICATION:     { bg: "#F0FDF4", text: "#166534" },
  MARGIN_THRESHOLD:           { bg: "#FFFBEB", text: "#D97706" },
  FINANCIAL_EXCEPTION_REASON: { bg: "#FEF2F2", text: "#DC2626" },
  REPORTING_DIMENSION:        { bg: "#F5F3FF", text: "#7C3AED" },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function TypeBadge({ type }) {
  const c = TYPE_BADGE_COLOR[type] || { bg: "#F3F4F6", text: "#374151" };
  const label = CONFIG_TYPES.find(t => t.id === type)?.label || type;
  return (
    <span style={{ background: c.bg, color: c.text, padding: "2px 8px", borderRadius: 8, fontSize: 11, fontWeight: 700 }}>
      {label}
    </span>
  );
}

function StatusPill({ active }) {
  return (
    <span style={{
      padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700,
      background: active ? "#F0FDF4" : "#F9FAFB",
      color: active ? "#166534" : "#9CA3AF",
      border: `1px solid ${active ? "#BBF7D0" : "#E5E7EB"}`,
    }}>
      {active ? "ACTIVE" : "INACTIVE"}
    </span>
  );
}

const EMPTY_FORM = {
  item_code: "", display_name: "", description: "",
  sort_order: "99", effective_from: "", effective_to: "",
  metadata: "{}",
};

// ── Main Component ────────────────────────────────────────────────────────────

export default function EventFinanceMDMTab() {
  const { user } = useAuth();
  const caps = user?.capabilities || [];
  const canView   = caps.includes("event.finance.view") || caps.includes("event.finance.manage");
  const canManage = caps.includes("event.finance.manage");

  const [selectedType,  setSelectedType]  = useState(CONFIG_TYPES[0].id);
  const [items,         setItems]         = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [search,        setSearch]        = useState("");
  const [showInactive,  setShowInactive]  = useState(false);
  const [totalByType,   setTotalByType]   = useState({});

  // Detail / edit / history panels
  const [detailItem,    setDetailItem]    = useState(null);
  const [historyData,   setHistoryData]   = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showCreate,    setShowCreate]    = useState(false);
  const [createForm,    setCreateForm]    = useState(EMPTY_FORM);
  const [editItem,      setEditItem]      = useState(null);
  const [editForm,      setEditForm]      = useState(null);
  const [saving,        setSaving]        = useState(false);
  const [deactivateConfirm, setDeactivateConfirm] = useState(null);

  const loadItems = useCallback(async (type) => {
    if (!canView) return;
    setLoading(true);
    setDetailItem(null);
    setHistoryData(null);
    try {
      const res = await eventFinanceApi.listMDM(type, { include_inactive: showInactive });
      const list = res.data?.items || [];
      setItems(list);
      setTotalByType(prev => ({ ...prev, [type]: list.length }));
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  }, [canView, showInactive]);

  useEffect(() => { loadItems(selectedType); }, [selectedType, showInactive]);

  const handleTypeChange = (tid) => {
    setSelectedType(tid);
    setSearch("");
  };

  // Create
  const handleCreate = async () => {
    setSaving(true);
    try {
      let metadata = {};
      try { metadata = JSON.parse(createForm.metadata || "{}"); } catch { toast.error("Metadata must be valid JSON"); setSaving(false); return; }
      await eventFinanceApi.createMDM(selectedType, {
        item_code:      createForm.item_code.trim().toUpperCase(),
        display_name:   createForm.display_name.trim(),
        description:    createForm.description.trim() || undefined,
        sort_order:     parseInt(createForm.sort_order) || 99,
        effective_from: createForm.effective_from || undefined,
        effective_to:   createForm.effective_to || undefined,
        metadata,
      });
      toast.success(`${selectedType} item created`);
      setShowCreate(false);
      setCreateForm(EMPTY_FORM);
      loadItems(selectedType);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  // Edit
  const openEdit = (item) => {
    setEditItem(item);
    setEditForm({
      display_name:   item.display_name || "",
      description:    item.description || "",
      sort_order:     String(item.sort_order ?? 99),
      effective_from: item.effective_from || "",
      effective_to:   item.effective_to || "",
      metadata:       JSON.stringify(item.metadata || {}, null, 2),
    });
  };

  const handleEditSave = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      let metadata;
      try { metadata = JSON.parse(editForm.metadata || "{}"); } catch { toast.error("Metadata must be valid JSON"); setSaving(false); return; }
      await eventFinanceApi.updateMDM(selectedType, editItem.id, {
        display_name:   editForm.display_name.trim() || undefined,
        description:    editForm.description.trim() || undefined,
        sort_order:     parseInt(editForm.sort_order) || undefined,
        effective_from: editForm.effective_from || undefined,
        effective_to:   editForm.effective_to || undefined,
        metadata,
      });
      toast.success("MDM item updated");
      setEditItem(null);
      setEditForm(null);
      loadItems(selectedType);
      if (detailItem?.id === editItem.id) {
        const res = await eventFinanceApi.getMDMItem(selectedType, editItem.id);
        setDetailItem(res.data);
      }
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  // Activate / deactivate
  const handleDeactivate = async (item) => {
    try {
      await eventFinanceApi.deactivateMDM(selectedType, item.id, { reason: "Deactivated via Finance Config admin panel" });
      toast.success(`${item.item_code} deactivated`);
      setDeactivateConfirm(null);
      if (detailItem?.id === item.id) setDetailItem(prev => ({ ...prev, is_active: false }));
      loadItems(selectedType);
    } catch (err) { toast.error(formatError(err)); }
  };

  const handleActivate = async (item) => {
    try {
      await eventFinanceApi.activateMDM(selectedType, item.id);
      toast.success(`${item.item_code} activated`);
      if (detailItem?.id === item.id) setDetailItem(prev => ({ ...prev, is_active: true }));
      loadItems(selectedType);
    } catch (err) { toast.error(formatError(err)); }
  };

  // Detail + history
  const openDetail = async (item) => {
    setDetailItem(item);
    setHistoryData(null);
  };

  const loadHistory = async (item) => {
    setHistoryLoading(true);
    try {
      const res = await eventFinanceApi.getMDMHistory(selectedType, item.id);
      setHistoryData(res.data);
    } catch (err) { toast.error(formatError(err)); }
    finally { setHistoryLoading(false); }
  };

  // Export as CSV
  const handleExport = () => {
    const rows = [
      ["item_code", "display_name", "description", "sort_order", "effective_from", "effective_to", "is_active", "created_by", "created_at"],
      ...items.map(i => [
        i.item_code, i.display_name, i.description || "",
        i.sort_order, i.effective_from || "", i.effective_to || "",
        i.is_active, i.created_by || "", (i.created_at || "").slice(0, 10),
      ]),
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `efin_mdm_${selectedType.toLowerCase()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = items.filter(i => {
    if (!search) return true;
    const q = search.toLowerCase();
    return i.item_code?.toLowerCase().includes(q) || i.display_name?.toLowerCase().includes(q) || i.description?.toLowerCase().includes(q);
  });

  if (!canView) {
    return (
      <div data-testid="efin-mdm-no-access" style={{ padding: 32, textAlign: "center" }}>
        <Shield size={28} color="#DC2626" />
        <p style={{ color: "#6B7280", marginTop: 8, fontSize: 13 }}>event.finance.view capability required to access Event Finance Configuration.</p>
      </div>
    );
  }

  const selectedTypeMeta = CONFIG_TYPES.find(t => t.id === selectedType);

  return (
    <div data-testid="efin-mdm-tab" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: "#181818", margin: 0 }}>Event Finance Configuration</h2>
          <p style={{ fontSize: 12, color: "#706E6B", marginTop: 3 }}>
            MDM-STD-01 — master reference data for EFIN metrics, allocation rules and reporting dimensions.
            {!canManage && <span style={{ color: "#D97706", marginLeft: 6 }}>(Read-only — event.finance.manage required to edit)</span>}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            data-testid="efin-mdm-toggle-inactive"
            onClick={() => setShowInactive(v => !v)}
            style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 8, border: "1px solid #E2E8F0", background: showInactive ? "#F0FDF4" : "#FFF", fontSize: 12, cursor: "pointer", color: showInactive ? "#166534" : "#374151" }}>
            {showInactive ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
            {showInactive ? "Showing all" : "Active only"}
          </button>
          <button
            data-testid="efin-mdm-export-btn"
            onClick={handleExport}
            style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#FFF", fontSize: 12, cursor: "pointer", color: "#374151" }}>
            <Download size={13} /> Export CSV
          </button>
          {canManage && (
            <button
              data-testid="efin-mdm-create-btn"
              onClick={() => { setShowCreate(true); setCreateForm(EMPTY_FORM); }}
              style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 8, border: "none", background: "#0176D3", color: "#FFF", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              <Plus size={13} /> New Item
            </button>
          )}
        </div>
      </div>

      {/* Config Type Selector */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {CONFIG_TYPES.map(ct => {
          const isSelected = selectedType === ct.id;
          const c = TYPE_BADGE_COLOR[ct.id];
          return (
            <button
              key={ct.id}
              data-testid={`efin-mdm-type-${ct.id.toLowerCase()}`}
              onClick={() => handleTypeChange(ct.id)}
              style={{
                padding: "6px 12px", borderRadius: 8,
                border: isSelected ? `1.5px solid ${c.text}` : "1px solid #E2E8F0",
                background: isSelected ? c.bg : "#FFF",
                color: isSelected ? c.text : "#6B7280",
                fontSize: 12, fontWeight: isSelected ? 700 : 400,
                cursor: "pointer", transition: "all 0.15s",
              }}>
              {ct.label}
              <span style={{ marginLeft: 5, fontSize: 10, opacity: 0.7 }}>
                {totalByType[ct.id] != null ? `(${totalByType[ct.id]})` : ""}
              </span>
            </button>
          );
        })}
      </div>

      {/* Selected type description */}
      <div style={{ padding: "8px 12px", background: "#F8FAFC", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12, color: "#706E6B", display: "flex", gap: 8, alignItems: "center" }}>
        <Info size={13} color="#0176D3" />
        <span>{selectedTypeMeta?.description}</span>
      </div>

      {/* Search bar */}
      <div style={{ position: "relative" }}>
        <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9CA3AF" }} />
        <input
          data-testid="efin-mdm-search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search code, name or description…"
          style={{ width: "100%", paddingLeft: 30, paddingRight: 12, paddingTop: 8, paddingBottom: 8, borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13, boxSizing: "border-box", outline: "none" }}
        />
      </div>

      {/* Main layout: list + optional detail panel */}
      <div style={{ display: "grid", gridTemplateColumns: detailItem ? "1fr 380px" : "1fr", gap: 14 }}>

        {/* Items list */}
        <div style={{ background: "#FFF", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: "10px 16px", borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "center", gap: 8 }}>
            <TypeBadge type={selectedType} />
            <span style={{ fontSize: 12, color: "#6B7280" }}>{filtered.length} item{filtered.length !== 1 ? "s" : ""}</span>
            <button onClick={() => loadItems(selectedType)} disabled={loading}
              style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 6, border: "1px solid #E2E8F0", background: "#FFF", fontSize: 11, cursor: "pointer" }}>
              <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>

          {loading ? (
            <div style={{ padding: 32, textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>
              {search ? "No items match the search." : "No items configured for this type yet."}
            </div>
          ) : (
            <table data-testid="efin-mdm-items-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#F8FAFC" }}>
                  {["Code", "Display Name", "Eff. From", "Status", "Actions"].map(h => (
                    <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#374151", borderBottom: "1px solid #E2E8F0" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((item, i) => (
                  <tr key={item.id} data-testid={`efin-mdm-row-${i}`}
                    style={{ borderBottom: "1px solid #F3F4F6", background: detailItem?.id === item.id ? "#EEF4FF" : "transparent" }}>
                    <td style={{ padding: "8px 12px", fontFamily: "monospace", fontWeight: 700, color: "#374151" }}>{item.item_code}</td>
                    <td style={{ padding: "8px 12px", color: "#181818" }}>
                      {item.display_name}
                      {item.description && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 1 }}>{item.description.slice(0, 60)}{item.description.length > 60 ? "…" : ""}</div>}
                    </td>
                    <td style={{ padding: "8px 12px", color: "#6B7280", fontFamily: "monospace", fontSize: 11 }}>{item.effective_from || "—"}</td>
                    <td style={{ padding: "8px 12px" }}><StatusPill active={item.is_active} /></td>
                    <td style={{ padding: "8px 12px" }}>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button data-testid={`efin-mdm-detail-${i}`} onClick={() => openDetail(item)}
                          style={{ padding: "3px 8px", borderRadius: 5, border: "1px solid #E2E8F0", background: "#F9FAFB", fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", gap: 3 }}>
                          <ChevronRight size={10} /> View
                        </button>
                        {canManage && (
                          <button data-testid={`efin-mdm-edit-${i}`} onClick={() => openEdit(item)}
                            style={{ padding: "3px 8px", borderRadius: 5, border: "1px solid #BFDBFE", background: "#EEF4FF", color: "#0176D3", fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", gap: 3 }}>
                            <Edit3 size={10} /> Edit
                          </button>
                        )}
                        {canManage && item.is_active && item.tenant_id !== "PLATFORM" && (
                          <button data-testid={`efin-mdm-deactivate-${i}`} onClick={() => setDeactivateConfirm(item)}
                            style={{ padding: "3px 8px", borderRadius: 5, border: "1px solid #FECACA", background: "#FEF2F2", color: "#DC2626", fontSize: 10, cursor: "pointer" }}>
                            Deactivate
                          </button>
                        )}
                        {canManage && !item.is_active && (
                          <button data-testid={`efin-mdm-activate-${i}`} onClick={() => handleActivate(item)}
                            style={{ padding: "3px 8px", borderRadius: 5, border: "1px solid #BBF7D0", background: "#F0FDF4", color: "#166534", fontSize: 10, cursor: "pointer" }}>
                            Activate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Detail panel */}
        {detailItem && (
          <div data-testid="efin-mdm-detail-panel" style={{ background: "#FFF", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden", alignSelf: "flex-start" }}>
            <div style={{ padding: "10px 14px", borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: "#181818", flex: 1 }}>{detailItem.item_code}</span>
              {canManage && (
                <button onClick={() => openEdit(detailItem)}
                  style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid #BFDBFE", background: "#EEF4FF", color: "#0176D3", fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", gap: 3 }}>
                  <Edit3 size={11} /> Edit
                </button>
              )}
              <button onClick={() => { setDetailItem(null); setHistoryData(null); }}
                style={{ padding: 4, borderRadius: 6, border: "none", background: "transparent", cursor: "pointer", color: "#9CA3AF" }}>
                <X size={14} />
              </button>
            </div>

            <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
              {[
                ["Display Name", detailItem.display_name],
                ["Description", detailItem.description || "—"],
                ["Sort Order", detailItem.sort_order],
                ["Effective From", detailItem.effective_from || "—"],
                ["Effective To", detailItem.effective_to || "—"],
                ["Status", <StatusPill key="s" active={detailItem.is_active} />],
                ["Created By", detailItem.created_by || "—"],
                ["Created", (detailItem.created_at || "").slice(0, 16)],
                ["Updated", (detailItem.updated_at || "").slice(0, 16)],
                ["Tenant Scope", detailItem.tenant_id === "PLATFORM" ? "PLATFORM (protected)" : "Tenant"],
              ].map(([label, val]) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "1px solid #F3F4F6", paddingBottom: 8, gap: 8 }}>
                  <span style={{ fontSize: 11, color: "#9CA3AF", flexShrink: 0, minWidth: 90, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
                  <span style={{ fontSize: 12, color: "#181818", textAlign: "right", wordBreak: "break-all" }}>{val}</span>
                </div>
              ))}

              {/* Metadata */}
              {detailItem.metadata && Object.keys(detailItem.metadata).length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>Metadata</div>
                  <pre style={{ fontSize: 10, background: "#F8FAFC", borderRadius: 6, padding: 8, border: "1px solid #E2E8F0", overflow: "auto", maxHeight: 100, color: "#374151" }}>
                    {JSON.stringify(detailItem.metadata, null, 2)}
                  </pre>
                </div>
              )}

              {/* Audit History */}
              <div>
                <button
                  data-testid="efin-mdm-history-btn"
                  onClick={() => historyData ? setHistoryData(null) : loadHistory(detailItem)}
                  style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 6, border: "1px solid #E2E8F0", background: "#F8FAFC", fontSize: 11, cursor: "pointer", color: "#374151", width: "100%" }}>
                  <History size={12} /> {historyData ? "Hide History" : "View Audit History"}
                </button>

                {historyLoading && <div style={{ textAlign: "center", padding: 8, color: "#9CA3AF", fontSize: 12 }}>Loading…</div>}

                {historyData && !historyLoading && (
                  <div style={{ marginTop: 8, background: "#F8FAFC", borderRadius: 8, border: "1px solid #E2E8F0", padding: "8px 10px" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 6 }}>
                      {historyData.total_revisions} revision{historyData.total_revisions !== 1 ? "s" : ""}
                    </div>
                    {historyData.total_revisions === 0 ? (
                      <div style={{ fontSize: 11, color: "#9CA3AF" }}>No revisions recorded.</div>
                    ) : (
                      historyData.revision_history.map((rev, i) => (
                        <div key={i} data-testid={`efin-mdm-history-row-${i}`} style={{ fontSize: 11, color: "#374151", borderBottom: "1px solid #E2E8F0", paddingBottom: 5, marginBottom: 5 }}>
                          <div style={{ fontFamily: "monospace", color: "#6B7280" }}>{(rev.changed_at || "").slice(0, 16)}</div>
                          <div>{rev.changed_by_email} · {(rev.changed_fields || []).join(", ")}</div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Platform protection notice */}
              {detailItem.tenant_id === "PLATFORM" && (
                <div style={{ padding: "8px 10px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, fontSize: 11, color: "#92400E", display: "flex", gap: 6 }}>
                  <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 1 }} />
                  Platform-managed item. Deactivation by tenant is not permitted.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Create Modal ─────────────────────────────────────────────────────── */}
      {showCreate && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#FFF", borderRadius: 16, border: "1px solid #E2E8F0", padding: 24, width: "100%", maxWidth: 480, maxHeight: "80vh", overflowY: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: "#181818", flex: 1 }}>New <TypeBadge type={selectedType} /> Item</span>
              <button onClick={() => setShowCreate(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF" }}><X size={16} /></button>
            </div>
            <MDMForm
              form={createForm}
              onChange={setCreateForm}
              onSave={handleCreate}
              onCancel={() => setShowCreate(false)}
              saving={saving}
              isCreate
            />
          </div>
        </div>
      )}

      {/* ── Edit Modal ───────────────────────────────────────────────────────── */}
      {editItem && editForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#FFF", borderRadius: 16, border: "1px solid #E2E8F0", padding: 24, width: "100%", maxWidth: 480, maxHeight: "80vh", overflowY: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: "#181818", flex: 1 }}>Edit — {editItem.item_code}</span>
              <button onClick={() => { setEditItem(null); setEditForm(null); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF" }}><X size={16} /></button>
            </div>
            <MDMForm
              form={editForm}
              onChange={setEditForm}
              onSave={handleEditSave}
              onCancel={() => { setEditItem(null); setEditForm(null); }}
              saving={saving}
              isCreate={false}
            />
          </div>
        </div>
      )}

      {/* ── Deactivate Confirmation ──────────────────────────────────────────── */}
      {deactivateConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#FFF", borderRadius: 16, border: "1px solid #E2E8F0", padding: 24, width: "100%", maxWidth: 380 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <AlertTriangle size={20} color="#D97706" />
              <span style={{ fontWeight: 700, fontSize: 14, color: "#181818" }}>Deactivate {deactivateConfirm.item_code}?</span>
            </div>
            <p style={{ fontSize: 13, color: "#6B7280", marginBottom: 16 }}>
              This will mark the item as INACTIVE. Existing records referencing this code are not affected. You can reactivate it at any time.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setDeactivateConfirm(null)}
                style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "1px solid #E2E8F0", background: "#FFF", fontSize: 13, cursor: "pointer" }}>
                Cancel
              </button>
              <button data-testid="confirm-deactivate-btn" onClick={() => handleDeactivate(deactivateConfirm)}
                style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "none", background: "#DC2626", color: "#FFF", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restrictions footer */}
      <div style={{ fontSize: 11, color: "#9CA3AF", background: "#F8FAFC", borderRadius: 8, padding: "8px 12px", border: "1px solid #E2E8F0" }}>
        <strong>MDM-STD-01:</strong> event.finance.manage required to create/edit/toggle. event.finance.manage does NOT grant shared-cost allocation approval or reversal. Platform items are protected.
      </div>
    </div>
  );
}

// ── Form sub-component ────────────────────────────────────────────────────────

function MDMForm({ form, onChange, onSave, onCancel, saving, isCreate }) {
  const set = (k, v) => onChange(f => ({ ...f, [k]: v }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {isCreate && (
        <MDMField label="Item Code *" helpText="Uppercase, e.g. CAT-EVENT-STAGING. Immutable after creation.">
          <input data-testid="mdm-item-code" value={form.item_code} onChange={e => set("item_code", e.target.value.toUpperCase().replace(/\s/g, "_"))}
            placeholder="CAT_STAGING" style={inputStyle} required />
        </MDMField>
      )}
      <MDMField label="Display Name *">
        <input data-testid="mdm-display-name" value={form.display_name} onChange={e => set("display_name", e.target.value)}
          placeholder="Staging & Venue" style={inputStyle} required />
      </MDMField>
      <MDMField label="Description">
        <textarea data-testid="mdm-description" value={form.description} onChange={e => set("description", e.target.value)}
          placeholder="Optional description for this item" rows={2} style={{ ...inputStyle, resize: "vertical" }} />
      </MDMField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <MDMField label="Sort Order">
          <input data-testid="mdm-sort-order" type="number" value={form.sort_order} onChange={e => set("sort_order", e.target.value)}
            style={inputStyle} />
        </MDMField>
        <div /> {/* spacer */}
        <MDMField label="Effective From">
          <input data-testid="mdm-effective-from" type="date" value={form.effective_from} onChange={e => set("effective_from", e.target.value)}
            style={inputStyle} />
        </MDMField>
        <MDMField label="Effective To">
          <input data-testid="mdm-effective-to" type="date" value={form.effective_to} onChange={e => set("effective_to", e.target.value)}
            style={inputStyle} />
        </MDMField>
      </div>
      <MDMField label="Metadata (JSON)" helpText="Optional structured metadata. Must be valid JSON object.">
        <textarea data-testid="mdm-metadata" value={form.metadata} onChange={e => set("metadata", e.target.value)}
          rows={3} style={{ ...inputStyle, fontFamily: "monospace", resize: "vertical" }} />
      </MDMField>

      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
        <button onClick={onCancel} style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "1px solid #E2E8F0", background: "#FFF", fontSize: 13, cursor: "pointer" }}>
          Cancel
        </button>
        <button data-testid="mdm-save-btn" onClick={onSave} disabled={saving}
          style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "none", background: "#0176D3", color: "#FFF", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: saving ? 0.6 : 1 }}>
          {saving ? "Saving…" : isCreate ? "Create Item" : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

function MDMField({ label, helpText, children }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 11, color: "#6B7280", marginBottom: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
      </label>
      {children}
      {helpText && <p style={{ fontSize: 10, color: "#9CA3AF", marginTop: 3 }}>{helpText}</p>}
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "7px 10px",
  borderRadius: 8,
  border: "1px solid #E2E8F0",
  fontSize: 13,
  color: "#181818",
  background: "#F8FAFC",
  outline: "none",
  boxSizing: "border-box",
};
